import { AnalyticsService } from 'base/analytics';
import { keyOfObject } from 'base/helpers';
import { Button, ButtonTextareaContainer } from 'base/ui/button';
import { createDndDomain } from 'base/ui/dnd';
import { IconAdd, IconBurger2, IconSitemap } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { DropdownIcon, useRightClickMenu } from 'base/ui/menu';
import { useButtonWheelBehaviour } from 'base/ui/select';
import { ClickInTextarea } from 'base/ui/textarea';
import { TextSmall } from 'base/ui/typography';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { ProjectEditingActions } from './editing_actions/project';
import { aSpace, Space, spaceDomain, SpaceState } from './model';
import { DEFAULT_HOME_SPACE_NAME } from './model_constants';
import { useClickOrHoverToSelect } from './selection/selectable';
import { SelectionStore } from './selection/store';
import { spaceEditMenu } from './sitemap/space_common';
import styles from './space_selector.css';

type SpaceDragSource = {
  space: SpaceState;
  index: number;
  ref: Space | 'home';
};
export const spaceDndDomain = createDndDomain<SpaceDragSource, number>({
  isSelfDrop: (source, dest) =>
    source.index === dest || source.index + 1 === dest,
});

export const SpaceSelector = observer(function _SpaceSelector({
  selection,
  editing,
  analytics,
}: {
  selection: SelectionStore;
  editing: ProjectEditingActions | undefined;
  analytics: AnalyticsService;
}) {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const dndState = spaceDndDomain.state.get();
  function renderDropPreview(index: number) {
    if (dndState?.dest !== index) return null;
    return (
      <spaceDndDomain.DropTarget
        opts={
          editing && {
            mouseToDestination: () => dndState.dest,
            processDrop: editing.dropSpaceAt,
          }
        }
        className={styles.imposter}
        style={{ width: dndState.drag.width, height: dndState.drag.height }}
      />
    );
  }

  const sel = selection.get();
  const selectedName =
    sel.space === 'home' || sel.space == null
      ? selection.project.homeSpaceName || DEFAULT_HOME_SPACE_NAME
      : sel.space.name;

  const onWheel = useButtonWheelBehaviour((direction) => {
    const items = ['home' as const, ...selection.project.spaces];
    const currentSpace = sel.space || 'home';
    const currentIndex = items.indexOf(currentSpace);
    const nextIndex = (currentIndex + direction + items.length) % items.length;
    const nextSpace = items[nextIndex];
    if (nextSpace !== currentSpace) {
      selection.set({ space: nextSpace });
    }
  });

  return (
    <>
      <div className={styles.spaceSelector}>
        <div className={styles.mobileButton}>
          <Button
            variant="secondary"
            background="grey3"
            xalign="left"
            label={selectedName}
            checked={mobileOpen}
            iconRight={DropdownIcon}
            onClick={() => setMobileOpen((o) => !o)}
          />
        </div>
        <div className={styles.desktopButton}>
          <Button
            variant="secondary"
            background="grey3"
            xalign="left"
            label={selectedName}
            icon={IconSitemap}
            iconRight={IconBurger2}
            onWheel={onWheel}
          />
        </div>
        <div
          className={classNames(
            styles.spaceList,
            mobileOpen && styles.isMobileOpen,
          )}
        >
          {renderDropPreview(0)}
          <spaceDndDomain.Draggable
            source={{
              space: aSpace({
                name:
                  selection.project.homeSpaceName ?? DEFAULT_HOME_SPACE_NAME,
                subPages: [selection.project.homePage.state],
              }),
              index: 0,
              ref: 'home',
            }}
            selfDropTarget={0}
            dropTarget={
              editing && {
                mouseToDestination: () => 0,
                processDrop: editing.dropSpaceAt,
              }
            }
          >
            <SpaceButton
              space="home"
              selection={selection}
              projectEditing={editing}
            />
          </spaceDndDomain.Draggable>

          {selection.project.spaces.map((space, i) => (
            <React.Fragment key={keyOfObject(space)}>
              {renderDropPreview(i + 1)}
              <spaceDndDomain.Draggable
                source={{ space: space.state, index: i + 1, ref: space }}
                selfDropTarget={i + 1}
                dropTarget={
                  editing && {
                    mouseToDestination: (_, clientY, el) => {
                      const rect = el.getBoundingClientRect();
                      const middle = rect.top + rect.height / 2;
                      return clientY < middle ? i + 1 : i + 2;
                    },
                    processDrop: editing.dropSpaceAt,
                  }
                }
              >
                <SpaceButton
                  space={space}
                  selection={selection}
                  projectEditing={editing}
                />
              </spaceDndDomain.Draggable>
            </React.Fragment>
          ))}
          {renderDropPreview(selection.project.spaces.length + 1)}

          {editing && (
            <spaceDndDomain.DropTarget
              className={styles.spaceDivider}
              opts={
                editing && {
                  mouseToDestination: () => selection.project.spaces.length + 1,
                  processDrop: editing.dropSpaceAt,
                }
              }
            >
              <Rows spacing={8}>
                <Button
                  icon={IconAdd}
                  fullWidth
                  variant="tertiary"
                  background="grey2"
                  onClick={() => {
                    editing.addSpaceToEnd();
                    analytics.track({
                      event: 'blocks/add-space',
                      method: 'blank',
                    });
                  }}
                />
              </Rows>
            </spaceDndDomain.DropTarget>
          )}
        </div>
      </div>
    </>
  );
});

const SpaceButton = observer(function _SpaceButton({
  space,
  selection,
  projectEditing,
}: {
  space: SpaceState | Space | 'home';
  selection: SelectionStore;
  projectEditing: ProjectEditingActions | undefined;
}) {
  const ref = React.useRef<HTMLButtonElement>(null);
  const [hasFocus, setHasFocus] = React.useState(false);

  useClickOrHoverToSelect(
    ref,
    selection,
    spaceDomain.isMutable(space) ? { space } : { space: 'home' as const },
    { ensureVisibleOnSelect: true, clickEventOnly: true },
  );

  const isChecked = (selection.get().space || 'home') === space;

  const editing =
    space === 'home' || space.mutable
      ? projectEditing?.forSpace(space)
      : undefined;
  const [onContextMenu, menuPortal] = useRightClickMenu(
    projectEditing && editing ? spaceEditMenu(projectEditing, editing) : [],
  );

  return (
    <div className={styles.spaceButton}>
      <Button
        ref={ref}
        checked={isChecked}
        onContextMenu={onContextMenu}
        fullWidth
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
      >
        <ButtonTextareaContainer>
          <TextSmall>
            <ClickInTextarea
              value={
                space === 'home'
                  ? selection.project.homeSpaceName ?? DEFAULT_HOME_SPACE_NAME
                  : space.name
              }
              onChange={editing?.setName}
              placeholder="Untitled Sitemap"
              variant="invisible"
              enableClickIn={hasFocus}
              lineClampWhenNotEditing={true}
            />
          </TextSmall>
        </ButtonTextareaContainer>
      </Button>
      {menuPortal}
    </div>
  );
});
